<template>
    <v-card class="mt-4">
        <v-card-title>
            <v-row align="center">
                <v-col class="grow ma-2"> Thing Date Estimates </v-col>
                <v-col class="shrink text-right">
                    <v-btn
                        color="primary"
                        class="ma-2"
                        @click="showCreateTaskThingDateEstimateForm"
                    >
                        New Estimated Thing Date
                    </v-btn>
                </v-col>
            </v-row>
        </v-card-title>
        <v-data-table
            :headers="taskThingDateEstimateHeaders"
            :items="taskThingDateEstimateItems"
            no-results-text="No task date estimates."
            item-key="id"
            class="elevation-1"
            density="compact"
            :items-per-page="25"
        >
            <template v-slot:item.edit="{ item }">
                <v-icon @click="showEditTaskThingDateEstimate(item.taskThingDateEstimate)"
                    >$pencil</v-icon
                >
            </template>
            <template v-slot:item.delete="{ item }">
                <v-icon @click="deleteTaskThingDateEstimate(item.taskThingDateEstimate)"
                    >$delete</v-icon
                >
            </template>
        </v-data-table>
        <v-dialog v-model="showTaskThingDateEstimateForm" width="1000" persistent>
            <task-thing-date-estimate-form
                v-if="selectedTaskThingDateEstimate !== null"
                :protocol="protocol"
                :experiment="experiment"
                :task-thing-date-estimate="selectedTaskThingDateEstimate"
                @closed="taskThingDateEstimateFormClosed"
                @saved="taskThingDateEstimateSaved"
            />
        </v-dialog>
    </v-card>
</template>
<script>
import { defineComponent } from "vue";

import TaskThingDateEstimateForm from "./TaskThingDateEstimateForm.vue";
import { buildTaskThingDateEstimatesAPIForExperiment } from "@/services/api.services";
import { EventBus } from "@/lib/event-bus";
import { getAttribute, getThingSchemaForId } from "@/lib/thingUtils";

export default defineComponent({
    components: { TaskThingDateEstimateForm },

    props: {
        taskThingDateEstimates: {
            type: Array,
            required: true,
        },
        protocol: {
            type: Object, // AbstractSchemaConfig
            required: true,
        },
        experiment: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            selectedTaskThingDateEstimate: null,
            showTaskThingDateEstimateForm: false,
            taskThingDateEstimateHeaders: [
                {
                    title: "Thing",
                    value: "thingName",
                    align: "left",
                    sortable: false,
                    width: "25%",
                },
                {
                    title: "Day",
                    value: "dayAndDate",
                    sortable: true,
                    align: "left",
                    width: "25%",
                },
                {
                    title: "Edit",
                    value: "edit",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
                {
                    title: "Delete",
                    value: "delete",
                    sortable: false,
                    align: "left",
                    width: "5%",
                },
            ],
        };
    },

    mounted() {
        this.taskThingDateEstimatesAPI = buildTaskThingDateEstimatesAPIForExperiment(
            this.experiment.code
        );
    },

    computed: {
        taskThingDateEstimateItems() {
            return this.taskThingDateEstimates.map((taskThingDateEstimate) => {
                const thingSchema = getThingSchemaForId(
                    this.protocol,
                    taskThingDateEstimate.thingSchemaId
                );
                const attribute = getAttribute(
                    thingSchema,
                    taskThingDateEstimate.dateAttributeName
                );
                const thingName = `${attribute.display_name} for Thing ${taskThingDateEstimate.thingId}`;
                const date = this.experiment.day0.plus({ days: taskThingDateEstimate.day });
                const dayAndDate = `Day ${taskThingDateEstimate.day} on ${date.toFormat("yyyy-MM-dd")}`;
                return {
                    id: taskThingDateEstimate.id,
                    thingName,
                    dayAndDate,
                    taskThingDateEstimate,
                };
            });
        },
    },

    methods: {
        showCreateTaskThingDateEstimateForm() {
            this.selectedTaskThingDateEstimate = {
                day: 0,
                thingId: null,
                thingSchemaId: null,
                dateAttributeName: null,
            };
            this.showTaskThingDateEstimateForm = true;
        },
        showEditTaskThingDateEstimate(taskThingDateEstimate) {
            this.selectedTaskThingDateEstimate = taskThingDateEstimate;
            this.showTaskThingDateEstimateForm = true;
        },
        taskThingDateEstimateFormClosed() {
            this.resetTaskThingDateEstimateForm();
        },
        taskThingDateEstimateSaved(taskThingDateEstimate) {
            this.resetTaskThingDateEstimateForm();
            this.$emit("saved", taskThingDateEstimate);
        },
        resetTaskThingDateEstimateForm() {
            this.selectedTaskThingDateEstimate = null;
            this.showTaskThingDateEstimateForm = false;
        },
        deleteTaskThingDateEstimate(taskThingDateEstimate) {
            if (confirm("Are you sure you want to delete this date estimate?")) {
                this.taskThingDateEstimatesAPI
                    .delete(taskThingDateEstimate.id)
                    .then(() => {
                        this.$emit("deleted", taskThingDateEstimate);
                    })
                    .catch((err) => {
                        EventBus.$emit("globalAlert", err);
                    });
            }
        },
    },
});
</script>
<style></style>
